import {createContext, useCallback, use, useEffect, useMemo, useRef, type ReactNode, useState} from 'react'
import type {ConcreteRequest} from 'relay-runtime'
import {DEFAULT_RELAY_CACHE_TTL} from '../helpers'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useRelayEnvironment} from 'react-relay'
import {fetchQueryWithCache} from '../query-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'

type RelayCachePreheatContextType = {
  markQueryForPreheating: (
    query: ConcreteRequest,
    variables: Record<string, unknown>,
    source: string,
    excludeFromCacheKey?: string[],
    highPriority?: boolean,
  ) => void
  preHeatQueries: () => void
}

type RelayCachePreheatContextProviderType = {
  children: ReactNode
}

// This is just a safeguard against extreme cases (like an issue that has a thousands of issues links in comments)
export const MAX_QUERIES_TO_MARK_FOR_PREHEATING = 50

const PREHEAT_REQUESTS_BATCH_SIZE = 4

// Hot cache preheating reads from IndexedDB (no network calls), so we can use a larger batch size
const HOT_CACHE_PREHEAT_BATCH_SIZE = 10

const RelayCachePreheatContext = createContext<RelayCachePreheatContextType | null>(null)

type QueriesToPreheat = Record<
  string,
  {query: ConcreteRequest; variables: Record<string, unknown>; source: string; excludeFromCacheKey?: string[]}
>

export function RelayCachePreheatContextProvider({children}: RelayCachePreheatContextProviderType) {
  const relayEnvironment = useRelayEnvironment()
  const queriesToPreheatRef = useRef<QueriesToPreheat>({})
  const queriesToPreheatInHotCacheRef = useRef<QueriesToPreheat>({})
  const keysRef = useRef<Set<string>>(new Set())
  const prioritizedKeysRef = useRef<Set<string>>(new Set())
  const preheatedRef = useRef<Set<string>>(new Set())
  const keysForHotCacheRef = useRef<Set<string>>(new Set())
  const preheatedHotCacheRef = useRef<Set<string>>(new Set())
  const [pending, setPending] = useState(false)
  const [pendingHotCache, setPendingHotCache] = useState(false)
  const isHotCacheEnabled = isFeatureEnabled('issues_react_hot_cache')

  const preheatQuery = useCallback(
    async (key: string, forHotCache: boolean): Promise<boolean> => {
      const hasKey = forHotCache ? preheatedHotCacheRef.current.has(key) : preheatedRef.current.has(key)
      if (!hasKey) {
        const queryToPreheat = forHotCache
          ? queriesToPreheatInHotCacheRef.current[key]
          : queriesToPreheatRef.current[key]
        if (queryToPreheat) {
          if (!forHotCache) {
            preheatedRef.current.add(key)
          }
          preheatedHotCacheRef.current.add(key)
          try {
            await fetchQueryWithCache(
              relayEnvironment,
              queryToPreheat.query,
              queryToPreheat.variables,
              {fetchPolicy: 'store-or-network'},
              DEFAULT_RELAY_CACHE_TTL,
              queryToPreheat.source,
              false,
              queryToPreheat.excludeFromCacheKey,
              forHotCache,
            ).toPromise()
          } catch {
            // Silently handle errors - preheat failures shouldn't break the app
          }
          return true
        }
      }
      return false
    },
    [relayEnvironment],
  )

  const markQueryForPreheating = useCallback(
    (
      query: ConcreteRequest,
      variables: Record<string, unknown>,
      source: string,
      excludeFromCacheKey?: string[],
      highPriority?: boolean,
    ) => {
      const queryName = query.fragment.name
      // generate a key from the query name and sorted variables to avoid duplicates
      const filteredVariables = {...variables}
      if (excludeFromCacheKey && excludeFromCacheKey.length > 0) {
        for (const key of excludeFromCacheKey) {
          delete filteredVariables[key]
        }
      }
      const key = `${queryName}#${JSON.stringify(
        Object.fromEntries(Object.entries(filteredVariables).sort(([a], [b]) => (a < b ? -1 : 1))),
      )}`
      if (isHotCacheEnabled && !keysForHotCacheRef.current.has(key)) {
        queriesToPreheatInHotCacheRef.current[key] = {query, variables, source, excludeFromCacheKey}
        keysForHotCacheRef.current.add(key)
      }
      if (highPriority) {
        if (!prioritizedKeysRef.current.has(key)) {
          queriesToPreheatRef.current[key] = {query, variables, source, excludeFromCacheKey}
          prioritizedKeysRef.current.add(key)
        }
      } else if (!keysRef.current.has(key)) {
        queriesToPreheatRef.current[key] = {query, variables, source, excludeFromCacheKey}
        keysRef.current.add(key)
      }
      if (isHotCacheEnabled) setPendingHotCache(true)
      setPending(true)
    },
    [isHotCacheEnabled],
  )

  const preheatQuerySet = useCallback(
    (promises: Array<Promise<boolean>>, querySet: Set<string>) => {
      for (const key of querySet) {
        if (promises.length >= PREHEAT_REQUESTS_BATCH_SIZE) {
          break
        }
        if (!preheatedRef.current.has(key)) {
          promises.push(preheatQuery(key, false))
        }
      }
      return promises
    },
    [preheatQuery],
  )

  const preHeatQueries = useCallback(async () => {
    const promises = preheatQuerySet([], prioritizedKeysRef.current)
    preheatQuerySet(promises, keysRef.current)
    await Promise.all(promises)
    if (preheatedRef.current.size >= prioritizedKeysRef.current.size + keysRef.current.size) {
      // All queries have been preheated
      setPending(false)
    }
  }, [preheatQuerySet])

  const deferHotCachePreheating = isFeatureEnabled('issues_react_defer_hot_cache_preheating')

  useEffect(() => {
    let cancelled = false

    const scheduleNextPreheat = () => {
      if (cancelled || !('requestIdleCallback' in window)) return

      requestIdleCallback(() => {
        if (cancelled || !pending) return
        const runPreheat = async () => {
          await preHeatQueries()
          scheduleNextPreheat()
        }
        void runPreheat()
      })
    }

    scheduleNextPreheat()

    return () => {
      cancelled = true
    }
  }, [preHeatQueries, pending])

  useEffect(() => {
    if (!pendingHotCache || !isHotCacheEnabled) return

    let cancelled = false

    const waitForIdle = (): Promise<void> =>
      new Promise(resolve => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => resolve())
        } else {
          resolve()
        }
      })

    const run = async () => {
      const keys = [...keysForHotCacheRef.current].filter(key => !preheatedHotCacheRef.current.has(key))

      if (deferHotCachePreheating) {
        // Process in batches of HOT_CACHE_PREHEAT_BATCH_SIZE, yielding to idle between each
        for (let i = 0; i < keys.length; i += HOT_CACHE_PREHEAT_BATCH_SIZE) {
          if (cancelled) return
          await waitForIdle()
          if (cancelled) return
          const batch = keys.slice(i, i + HOT_CACHE_PREHEAT_BATCH_SIZE)
          await Promise.all(batch.map(key => preheatQuery(key, true)))
        }
      } else {
        // Legacy behavior: fire all immediately without batching or idle scheduling
        await Promise.all(keys.map(key => preheatQuery(key, true)))
      }

      setPendingHotCache(false)
    }

    void run()

    return () => {
      cancelled = true
    }
  }, [pendingHotCache, preheatQuery, deferHotCachePreheating, isHotCacheEnabled])

  const contextValue: RelayCachePreheatContextType = useMemo(() => {
    return {
      markQueryForPreheating,
      preHeatQueries,
    }
  }, [markQueryForPreheating, preHeatQueries])

  return <RelayCachePreheatContext value={contextValue}>{children}</RelayCachePreheatContext>
}

export const useRelayCachePreheatContext = () => {
  const context = use(RelayCachePreheatContext)
  if (!context) {
    // Fail silently to avoid issues in tests or if used outside of provider
    // eslint-disable-next-line no-console
    console.warn('useRelayCachePreheatContext must be used within a RelayCachePreheatContextProvider')
    return {
      markQueryForPreheating: () => {},
      preHeatQueries: () => {},
    }
  }

  return context
}
